import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import Switch from "@material-ui/core/Switch";
import Select from "@material-ui/core/Select";
import FormGroup from "@material-ui/core/FormGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import MenuItem from "@material-ui/core/MenuItem";
import sum from "lodash/sum";
import Hidden from "@material-ui/core/Hidden";
import IconButton from "@material-ui/core/IconButton";
import Icon from "@material-ui/core/Icon";
import Typography from "@material-ui/core/Typography";
import Popover from "@material-ui/core/Popover";
import EditIcon from "@material-ui/icons/Edit";
import { useDispatch, useSelector } from "react-redux";
import { updateGlobalParameter } from "../reducers/Artus/Actions";
import ConfirmationDialog from "./ConfirmationDialog";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "@material-ui/core";
import clsx from "clsx";

const useStyles = makeStyles(
  (theme) => ({
    root: {
      flexGrow: 1,
      backgroundColor: "#c6c7c9",
      margin: "0 20px",
      position: "relative",
      "& .MuiIconButton-root": {
        padding: "9px !important",
      },
    },
    row: {
      minHeight: 64,
      "& > .MuiFormControlLabel-root": {
        padding: "5px 10px",
        marginLeft: 0,
        marginRight: 0,
      },
    },
    switchControl: { marginLeft: "-8px !important" },
    switchThumbColor: {
      "& .MuiIconButton-root": {
        color: "#fafafa",
      },
    },
    popupRow: {
      flexDirection: "column",
      "& > .MuiFormControlLabel-root": {
        margin: "10px 0",
      },
      "& .MuiInputBase-root": {
        width: "100%",
        [theme.breakpoints.down("xs")]: {
          alignSelf: "flex-start",
          display: "flex",
          width: "auto",
        },
      },
    },
    popoverRootElement: {
      marginTop: 40,
      margin: "0px auto",
    },
    popupTitle: {
      fontWeight: "bold",
      padding: "5px 0",
    },
    popUpContent: {
      width: 300,
      backgroundColor: "#e8e9eb",
      padding: 20,
      "& .MuiIconButton-root": {
        padding: "9px !important",
      },
    },
    closeIcon: {
      position: "absolute",
      right: 20,
      top: 32,

      "&:hover": {
        cursor: "pointer",
      },
    },
    iconButton: {
      marginRight: 10,
      "& svg": {
        borderRadius: 4,
        boxShadow: "0px 2px 4px #00000080",
        backgroundColor: "#c5da86",
        borderColor: "#c5da86",
        padding: 12,
      },
    },
    edit: {
      display: "flex",
      alignItems: "center",
      height: 30,
    },
    popUpControlLabel: {
      width: "100%",
      paddingBottom: 5,
    },
    infoContainer: {
      position: "relative",
      backgroundColor: "white",
      height: 30,
    },
    paletteInfo: {
      position: "absolute",
      right: 0,
      bottom: -6,
      textAlign: "start",
      fontWeight: "bold",
      fontSize: 24,
    },
    select: {
      minWidth: 150,
      paddingTop: 8,
      borderBottom: 0,
      backgroundColor: "#fff",
      borderRadius: 4,
      "&:focus": {
        borderRadius: 4,
        backgroundColor: "#fff",
      },
      "&.popup": {
        minWidth: 200,
      },
    },
    colorBar: {
      opacity: 0.5,
    },
    controlLabel: {
      paddingRight: 10,
    },
  }),
  {
    name: "Settings",
  }
);

const GeneralSettingsArtus = (props) => {
  const classes = useStyles(props);
  const { t } = useTranslation(["settingsArtus", "common"]);
  const dispatch = useDispatch();
  useEffect(() => {
    !userUpdate && setSelectedFloor(floor);
  }, floor);
  const { fields, floor, doubleShelf, distance, scaleFactor } = useSelector((state) => state.Artus);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [userUpdate, setUserUpdate] = useState(false);
  const [selectedFloor, setSelectedFloor] = useState(floor);
  const [selectedScale, setSelectedScale] = useState(scaleFactor);
  const floorOptions = useSelector((state) => {
    return state.Configuration.artusComponentOptions.find((opt) => opt.key === "floor").options;
  });
  const palletSum = sum(
    fields.map((field) => {
      const fieldWidth = Number(field.width.match(/\d+/));
      const fieldSupportBars = Number(field.supportBars.match(/\d+/));
      const amountSingleShelf =
        (fieldWidth === 2700 ? 3 : fieldWidth === 3600 ? 4 : 2) * (fieldSupportBars + 1);
      const amountDoubleShelf = 2 * amountSingleShelf;
      return doubleShelf ? amountDoubleShelf : amountSingleShelf;
    })
  );
  const handleClose = () => {
    setAnchorElement(null);
  };

  const handleToggle = (event) => {
    setAnchorElement(event.target);
  };

  const [anchorElement, setAnchorElement] = useState(null);
  const open = Boolean(anchorElement);
  const settings = [
    {
      key: "shelf",
      title: t("common:doubleShelf"),
      selected: doubleShelf,
      visible: true,
      function: (e) => {
        dispatch(updateGlobalParameter("doubleShelf", e.target.checked));
      },
    },
    {
      key: "distance",
      title: t("distancePieces"),
      selected: distance,
      visible: doubleShelf,
      function: (e) => {
        dispatch(updateGlobalParameter("distance", e.target.value));
      },
      options: [
        {
          title: "200 mm",
          identifier: "200",
        },
        {
          title: "250 mm",
          identifier: "250",
        },
        {
          title: "300 mm",
          identifier: "300",
        },
      ],
    },
    {
      key: "floor",
      title: t("common:floor"),
      selected: floorOptions && floorOptions.length > 0 ? selectedFloor : "noBottom",
      showConfirmation: true,
      visible: true,
      function: (e) => {
        dispatch(updateGlobalParameter("floor", e.target.value));
      },
      options:
        floorOptions && floorOptions.length > 0
          ? floorOptions
          : [{ identifier: "noBottom", title: t("noFloor") }],
    },
  ];
  const updateValue = (setting, e) => {
    if (setting.key === "floor") {
      setUserUpdate(true);
      setSelectedFloor(e.target.value);
    }
    if (setting.showConfirmation) {
      setShowConfirmation(true);
    } else {
      dispatch(updateGlobalParameter(setting.key, Number(e.target.value)));
    }
  };
  const width = doubleShelf ? 780 : 470;
  const breakLayout = useMediaQuery(`(min-width:${width}px)`);

  return (
    <div className={classes.root}>
      {!breakLayout && (
        <div>
          <div className={classes.edit}>
            <IconButton classes={{ root: classes.iconButton }} onClick={handleToggle}>
              <EditIcon style={{ color: "#262626" }} />
            </IconButton>
            <Typography variant="body1">{t("common:settingsEdit")} </Typography>
          </div>
          <Popover
            classes={{ paper: classes.popoverRootElement }}
            onClose={handleClose}
            open={open}
            anchorEl={anchorElement}
          >
            <div className={clsx(classes.popUpContent, "popoverBorder")}>
              <Typography variant="body1" className={classes.popupTitle}>
                {t("common:settingsDialogtitle")}
              </Typography>
              <Icon className={classes.closeIcon} onClick={handleClose}>
                close
              </Icon>
              <FormGroup row className={classes.popupRow}>
                {settings.map(
                  (setting, index) =>
                    setting &&
                    setting.visible &&
                    (setting.options && setting.options.length > 0 ? (
                      <FormControlLabel
                        key={`setting_${index}`}
                        classes={{ label: classes.popUpControlLabel }}
                        control={
                          <Select
                            classes={{ root: clsx(classes.select, "popup") }}
                            variant="filled"
                            disableUnderline={true}
                            value={setting.selected}
                            onChange={(e) => {
                              updateValue(setting, e);
                            }}
                          >
                            {setting.options &&
                              setting.options.map((option, i) => (
                                <MenuItem
                                  className={classes.MenuItemOption}
                                  key={option.title + i}
                                  value={option.identifier}
                                >
                                  <Typography variant="body1" className={classes.toolTitle}>
                                    {option.title}
                                  </Typography>
                                </MenuItem>
                              ))}
                          </Select>
                        }
                        label={setting.title}
                        labelPlacement="top"
                      />
                    ) : (
                      <FormControlLabel
                        key={`setting_${index}`}
                        classes={{ root: classes.switchControl }}
                        control={
                          <Switch
                            classes={{
                              root: classes.switchThumbColor,
                              switchBase: doubleShelf ? "activeCheckedColor" : "",
                              checked: doubleShelf ? "activeCheckedColor" : "",
                              track: clsx(classes.colorBar, "primaryButton"),
                            }}
                            checked={doubleShelf}
                            onChange={setting.function}
                          />
                        }
                        label={setting.title}
                      />
                    ))
                )}
              </FormGroup>
            </div>
          </Popover>
        </div>
      )}
      {breakLayout && (
        <div>
          <FormGroup row className={classes.row}>
            {settings.map(
              (setting, index) =>
                setting &&
                setting.visible &&
                (setting.options && setting.options.length > 0 ? (
                  <FormControlLabel
                    key={`setting_${index}`}
                    classes={{ label: classes.controlLabel }}
                    control={
                      <Select
                        classes={{ root: classes.select }}
                        variant="filled"
                        disableUnderline={true}
                        value={setting.selected}
                        onChange={(e) => {
                          updateValue(setting, e);
                        }}
                      >
                        {setting.options &&
                          setting.options.map((option, i) => (
                            <MenuItem
                              className={classes.MenuItemOption}
                              key={option.title + i}
                              value={option.identifier}
                            >
                              <Typography variant="body1" className={classes.toolTitle}>
                                {option.title}
                              </Typography>
                            </MenuItem>
                          ))}
                      </Select>
                    }
                    label={setting.title}
                    labelPlacement="start"
                  />
                ) : (
                  <FormControlLabel
                    key={`setting_${index}`}
                    control={
                      <Switch
                        classes={{
                          root: classes.switchThumbColor,
                          switchBase: doubleShelf ? "activeCheckedColor" : "",
                          checked: doubleShelf ? "activeCheckedColor" : "",
                          track: clsx(classes.colorBar, "primaryButton"),
                        }}
                        checked={doubleShelf}
                        onChange={setting.function}
                      />
                    }
                    label={setting.title}
                  />
                ))
            )}
          </FormGroup>
        </div>
      )}
      {showConfirmation && (
        <ConfirmationDialog
          show={true}
          onCancel={() => {
            setUserUpdate(false);
            setSelectedFloor(floor);
            setShowConfirmation(false);
          }}
          onConfirm={() => {
            setUserUpdate(false);
            dispatch(updateGlobalParameter("floor", selectedFloor));
            setShowConfirmation(false);
          }}
          content={t("replaceAllBottoms")}
        />
      )}
      <Hidden xsDown>
        <div className={classes.infoContainer}>
          <Typography className={classes.paletteInfo} variant="body1">
            {t("palletBays", { palletSum: palletSum })}
          </Typography>
        </div>
      </Hidden>
    </div>
  );
};

GeneralSettingsArtus.propTypes = {
  settings: PropTypes.array,
};

GeneralSettingsArtus.defaultProps = {
  settings: [],
};

export default GeneralSettingsArtus;
